import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play,
  TestTube2,
  Search,
  Check,
  AlertCircle,
  Loader2,
  Cpu,
  Repeat,
  Save,
  Home,
  GitCompare,
  Activity,
  UsersRound,
  Settings,
  User,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { Breadcrumb } from '../../components/ui';

type RunType = 'MODEL_COMPARISON' | 'MODEL_STABILITY';

type LLMProvider = 'CLAUDE_OPUS_4_6' | 'GPT_5_2_PRO' | 'GEMINI_3_PRO';

// Grand Rounds expert configuration
interface GrandRoundsExpertModels {
  drPrecision: LLMProvider;
  drEvidence: LLMProvider;
  drAdvocate: LLMProvider;
}

// Grand Rounds prompt version - v1 (democracy) or v2 (joint-evidence)
type GrandRoundsPromptVersion = 'v1' | 'v2';

interface GrandRoundsConfig {
  expertModels: GrandRoundsExpertModels;
  moderatorModel: LLMProvider;
  promptVersion: GrandRoundsPromptVersion;
}

// Available models for Grand Rounds roles (excludes GRAND_ROUNDS itself)
const GRAND_ROUNDS_MODELS: { id: LLMProvider; name: string; color: string }[] = [
  { id: 'CLAUDE_OPUS_4_6', name: 'Claude Opus 4.6', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { id: 'GPT_5_2_PRO', name: 'GPT-5.2 Pro', color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'GEMINI_3_PRO', name: 'Gemini 3 Pro', color: 'bg-blue-100 text-blue-700 border-blue-200' },
];

// Expert role definitions
const EXPERT_ROLES = [
  { key: 'drPrecision' as const, name: 'Dr. Precision', role: 'Conservative Specialist', description: 'Focuses on patient safety and risk identification' },
  { key: 'drEvidence' as const, name: 'Dr. Evidence', role: 'Evidence-Based Practitioner', description: 'Cites research and clinical guidelines' },
  { key: 'drAdvocate' as const, name: 'Dr. Advocate', role: 'Patient-Centered Advocate', description: 'Considers accessibility and patient preferences' },
];

const DEFAULT_GRAND_ROUNDS_CONFIG: GrandRoundsConfig = {
  expertModels: {
    drPrecision: 'CLAUDE_OPUS_4_6',
    drEvidence: 'CLAUDE_OPUS_4_6',
    drAdvocate: 'CLAUDE_OPUS_4_6',
  },
  moderatorModel: 'CLAUDE_OPUS_4_6',
  promptVersion: 'v2', // Default to joint-evidence approach
};

interface TestRun {
  id: string;
  name: string;
  description?: string;
  status: string;
  testCaseId: string;
  runType: RunType;
  config: {
    models: string[];
    iterations: number;
  };
  testCase: {
    id: string;
    name: string;
    code: string;
    category: string;
    complexity: string;
    condition: string;
  };
}

interface TestCase {
  id: string;
  name: string;
  code: string;
  category: string;
  complexity: string;
  condition: string;
  isDraft: boolean;
  isActive: boolean;
  _count: { testRuns: number };
}

const MODELS = [
  {
    id: 'CLAUDE_OPUS_4_6',
    name: 'Claude Opus 4.6',
    provider: 'Anthropic',
    description: 'Latest Claude with adaptive thinking (max effort)',
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    icon: Cpu,
  },
  {
    id: 'GPT_5_2_PRO',
    name: 'GPT-5.2 Pro',
    provider: 'OpenAI',
    description: 'Latest GPT model with advanced reasoning',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: Cpu,
  },
  {
    id: 'GEMINI_3_PRO',
    name: 'Gemini 3 Pro',
    provider: 'Google',
    description: 'Multimodal model with strong medical knowledge',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Cpu,
  },
  {
    id: 'GRAND_ROUNDS',
    name: 'Grand Rounds',
    provider: 'Consensus',
    description: 'Multi-agent debate with expert personas (uses Claude)',
    color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    icon: UsersRound,
  },
];

export function TestRunCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: editId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const preselectedTestCaseId = searchParams.get('testCaseId');
  const isEditing = !!editId;

  const [search, setSearch] = useState('');
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string | null>(
    preselectedTestCaseId
  );
  const [runType, setRunType] = useState<RunType>('MODEL_COMPARISON');
  const [selectedModels, setSelectedModels] = useState<string[]>([
    'CLAUDE_OPUS_4_6',
    'GPT_5_2_PRO',
    'GEMINI_3_PRO',
  ]);
  const [iterations, setIterations] = useState(1); // Default to 1 for MODEL_COMPARISON
  const [runName, setRunName] = useState('');
  const [notes, setNotes] = useState('');
  const [isInitialized, setIsInitialized] = useState(!isEditing);
  const [grandRoundsConfig, setGrandRoundsConfig] = useState<GrandRoundsConfig>(DEFAULT_GRAND_ROUNDS_CONFIG);

  // Fetch existing test run if editing
  const { data: existingRunResponse, isLoading: loadingExistingRun } = useQuery({
    queryKey: ['validation', 'test-run', editId],
    queryFn: () => api.get<{ data: TestRun }>(`/api/validation/test-runs/${editId}`),
    enabled: isEditing,
  });

  // Initialize form with existing data when editing
  useEffect(() => {
    if (existingRunResponse?.data && !isInitialized) {
      const run = existingRunResponse.data;
      setSelectedTestCaseId(run.testCaseId);
      setRunType(run.runType || 'MODEL_COMPARISON');
      setSelectedModels(run.config.models || []);
      setIterations(run.config.iterations || 1);
      setRunName(run.name || '');
      setNotes(run.description || '');
      setIsInitialized(true);
    }
  }, [existingRunResponse, isInitialized]);

  // Reset iterations and models when run type changes
  useEffect(() => {
    if (runType === 'MODEL_COMPARISON') {
      setIterations(1);
      // Keep multiple models selected
    } else {
      // MODEL_STABILITY: keep only first model if multiple selected
      if (selectedModels.length > 1) {
        setSelectedModels([selectedModels[0]]);
      }
      // Set minimum 2 iterations for stability
      if (iterations < 2) {
        setIterations(3);
      }
    }
  }, [runType]);

  // Fetch test cases for selection
  const { data: testCasesData, isLoading: loadingTestCases } = useQuery({
    queryKey: ['validation', 'test-cases', 'active', search],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('isActive', 'true');
      params.set('isDraft', 'false');
      if (search) params.set('search', search);
      params.set('limit', '20');
      return api.get<{ data: TestCase[] }>(`/api/validation/test-cases?${params.toString()}`);
    },
  });

  // Fetch preselected test case details
  const { data: selectedTestCaseResponse } = useQuery({
    queryKey: ['validation', 'test-case', selectedTestCaseId],
    queryFn: () => api.get<{ data: TestCase }>(`/api/validation/test-cases/${selectedTestCaseId}`),
    enabled: !!selectedTestCaseId,
  });

  const selectedTestCase = selectedTestCaseResponse?.data;

  // Check if the selected test case is valid for creating a test run
  const isSelectedTestCaseValid = selectedTestCase
    ? !selectedTestCase.isDraft && selectedTestCase.isActive
    : true; // If not yet loaded, assume valid (will be validated on submit)

  const createMutation = useMutation({
    mutationFn: (data: {
      testCaseId: string;
      name: string;
      description?: string;
      runType: RunType;
      config: { models: string[]; iterations: number };
    }) => api.post<{ data: { id: string } }>('/api/validation/test-runs', data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] });
      navigate(`/validation/test-runs/${response.data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      runType?: RunType;
      config: { models: string[]; iterations: number };
    }) => api.patch<{ data: { id: string } }>(`/api/validation/test-runs/${editId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', editId] });
      navigate(`/validation/test-runs/${editId}`);
    },
  });

  const testCases = testCasesData?.data || [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTestCaseId) return;

    // Require at least one model
    if (selectedModels.length === 0) return;

    // MODEL_COMPARISON requires at least 2 models to compare
    if (runType === 'MODEL_COMPARISON' && selectedModels.length < 2) {
      alert('Model Comparison mode requires at least 2 models to compare. Please select more models or switch to Model Stability mode.');
      return;
    }

    // Validate selected test case is not a draft
    if (selectedTestCase && (selectedTestCase.isDraft || !selectedTestCase.isActive)) {
      alert('Cannot create a test run for a draft or inactive test case. Please activate the test case first.');
      return;
    }

    const name =
      runName.trim() ||
      `Run ${new Date().toLocaleDateString()} - ${selectedTestCase?.code || 'Unknown'}`;

    // Include grandRoundsConfig when GRAND_ROUNDS is selected
    const config: {
      models: string[];
      iterations: number;
      grandRoundsConfig?: GrandRoundsConfig;
    } = {
      models: selectedModels,
      iterations,
    };

    if (selectedModels.includes('GRAND_ROUNDS')) {
      config.grandRoundsConfig = grandRoundsConfig;
    }

    if (isEditing) {
      updateMutation.mutate({
        name,
        description: notes.trim() || undefined,
        runType,
        config,
      });
    } else {
      createMutation.mutate({
        testCaseId: selectedTestCaseId,
        name,
        description: notes.trim() || undefined,
        runType,
        config,
      });
    }
  };

  const toggleModel = (modelId: string) => {
    if (runType === 'MODEL_STABILITY') {
      // For stability tests, only one model allowed - replace current selection
      setSelectedModels([modelId]);
    } else {
      // For comparison tests, allow multiple selections
      setSelectedModels((prev) =>
        prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]
      );
    }
  };

  // Show loading state when editing and data not yet loaded
  if (isEditing && loadingExistingRun) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Model Validation', href: '/validation', icon: Home },
    { label: 'Test Runs', href: '/validation/test-runs', icon: Play },
    { label: isEditing ? 'Edit Test Run' : 'New Test Run' },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Test Run' : 'Create Test Run'}
        </h1>
        <p className="text-sm text-gray-500">
          {isEditing
            ? 'Modify the test run configuration before starting'
            : 'Execute a test case across multiple AI models'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Run Type Selection */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Activity className="h-5 w-5 text-amber-600" />
            Run Type
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            Choose the type of evaluation you want to perform.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setRunType('MODEL_COMPARISON')}
              className={clsx(
                'rounded-lg border-2 p-4 text-left transition-all',
                runType === 'MODEL_COMPARISON'
                  ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <div className="flex items-start gap-3">
                <GitCompare className={clsx(
                  'h-6 w-6 mt-0.5',
                  runType === 'MODEL_COMPARISON' ? 'text-amber-600' : 'text-gray-400'
                )} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Model Comparison</h3>
                    {runType === 'MODEL_COMPARISON' && (
                      <Check className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Compare accuracy across multiple AI models. Each model runs once.
                  </p>
                  <div className="mt-2 text-xs text-gray-400">
                    Multiple models • 1 iteration each
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setRunType('MODEL_STABILITY')}
              className={clsx(
                'rounded-lg border-2 p-4 text-left transition-all',
                runType === 'MODEL_STABILITY'
                  ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <div className="flex items-start gap-3">
                <Repeat className={clsx(
                  'h-6 w-6 mt-0.5',
                  runType === 'MODEL_STABILITY' ? 'text-amber-600' : 'text-gray-400'
                )} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Model Stability</h3>
                    {runType === 'MODEL_STABILITY' && (
                      <Check className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Evaluate consistency of a single model across multiple runs.
                  </p>
                  <div className="mt-2 text-xs text-gray-400">
                    1 model • Multiple iterations
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Step 1: Select Test Case */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <TestTube2 className="h-5 w-5 text-amber-600" />
            1. Select Test Case
          </h2>

          {selectedTestCase ? (
            <div className={clsx(
              'rounded-lg border p-4',
              isSelectedTestCaseValid
                ? 'border-amber-200 bg-amber-50'
                : 'border-red-200 bg-red-50'
            )}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{selectedTestCase.name}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600">
                      {selectedTestCase.code}
                    </span>
                    {selectedTestCase.isDraft && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        Draft
                      </span>
                    )}
                    {!selectedTestCase.isActive && !selectedTestCase.isDraft && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{selectedTestCase.condition}</p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                    <span>{selectedTestCase.category?.replace('_', ' ') || 'Unknown'}</span>
                    <span>•</span>
                    <span>{selectedTestCase.complexity || 'Unknown'}</span>
                    <span>•</span>
                    <span>{selectedTestCase._count?.testRuns ?? 0} previous runs</span>
                  </div>
                  {!isSelectedTestCaseValid && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        {selectedTestCase.isDraft
                          ? 'This test case is a draft. Complete and activate it before creating a test run.'
                          : 'This test case is inactive. Activate it before creating a test run.'}
                      </span>
                    </div>
                  )}
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => setSelectedTestCaseId(null)}
                    className="text-sm text-amber-600 hover:text-amber-700"
                  >
                    Change
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search test cases..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>

              {loadingTestCases ? (
                <div className="flex h-32 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : testCases.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
                  <TestTube2 className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">
                    {search ? 'No test cases match your search' : 'No active test cases available'}
                  </p>
                  <Link
                    to="/validation/test-cases/new"
                    className="mt-3 inline-flex items-center text-sm text-amber-600 hover:text-amber-700"
                  >
                    Create a test case first
                  </Link>
                </div>
              ) : (
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {testCases.map((tc) => (
                    <button
                      key={tc.id}
                      type="button"
                      onClick={() => setSelectedTestCaseId(tc.id)}
                      className={clsx(
                        'w-full rounded-lg border p-3 text-left transition-colors',
                        selectedTestCaseId === tc.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{tc.name}</span>
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600">
                              {tc.code}
                            </span>
                          </div>
                          <p className="mt-0.5 text-sm text-gray-500">{tc.condition}</p>
                        </div>
                        {selectedTestCaseId === tc.id && (
                          <Check className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 2: Select Models */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Cpu className="h-5 w-5 text-amber-600" />
            2. Select Model{runType === 'MODEL_COMPARISON' ? 's' : ''} to Test
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            {runType === 'MODEL_COMPARISON'
              ? 'Select one or more models to compare. Each model will run once and results will be evaluated blindly.'
              : 'Select the model to test for stability. It will run multiple times to measure consistency.'}
          </p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {MODELS.map((model) => {
              const Icon = model.icon;
              const isSelected = selectedModels.includes(model.id);
              const isGrandRounds = model.id === 'GRAND_ROUNDS';
              return (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => toggleModel(model.id)}
                  className={clsx(
                    'rounded-lg border-2 p-4 text-left transition-all',
                    isSelected
                      ? isGrandRounds
                        ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                        : 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span
                        className={clsx(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
                          model.color
                        )}
                      >
                        {model.provider}
                      </span>
                      <div className="mt-2 flex items-center gap-2">
                        <Icon className={clsx(
                          'h-4 w-4',
                          isSelected
                            ? isGrandRounds ? 'text-indigo-600' : 'text-amber-600'
                            : 'text-gray-400'
                        )} />
                        <h3 className="font-medium text-gray-900">{model.name}</h3>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">{model.description}</p>
                    </div>
                    {isSelected && (
                      <Check className={clsx(
                        'h-5 w-5',
                        isGrandRounds ? 'text-indigo-600' : 'text-amber-600'
                      )} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedModels.length === 0 && (
            <p className="mt-3 text-sm text-red-600">Select at least one model</p>
          )}
          {runType === 'MODEL_STABILITY' && selectedModels.length > 1 && (
            <p className="mt-3 text-sm text-amber-600">Stability tests use only one model. Only the first selected model will be used.</p>
          )}
          {selectedModels.includes('GRAND_ROUNDS') && (
            <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-indigo-600" />
                <p className="text-sm font-semibold text-indigo-900">Grand Rounds Configuration</p>
              </div>
              <p className="text-sm text-indigo-700">
                Configure which AI model runs each expert persona during the debate.
              </p>

              {/* Expert role configuration */}
              <div className="grid gap-3">
                {EXPERT_ROLES.map((role) => (
                  <div key={role.key} className="flex items-center justify-between gap-4 rounded-lg bg-white/60 p-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-200 flex-shrink-0">
                        <User className="h-4 w-4 text-indigo-700" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm">{role.name}</span>
                          <span className="text-xs text-gray-500">({role.role})</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{role.description}</p>
                      </div>
                    </div>
                    <div className="relative inline-block">
                      <select
                        value={grandRoundsConfig.expertModels[role.key]}
                        onChange={(e) => setGrandRoundsConfig(prev => ({
                          ...prev,
                          expertModels: {
                            ...prev.expertModels,
                            [role.key]: e.target.value as LLMProvider,
                          }
                        }))}
                        className="appearance-none rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2212%22%20height%3d%2212%22%20viewBox%3d%220%200%2012%2012%22%3e%3cpath%20fill%3d%22%236b7280%22%20d%3d%22M2%204l4%204%204-4%22%2f%3e%3c%2fsvg%3e')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
                      >
                        {GRAND_ROUNDS_MODELS.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}

                {/* Moderator configuration */}
                <div className="flex items-center justify-between gap-4 rounded-lg bg-white/60 p-3 border-t border-indigo-200 mt-2 pt-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-200 flex-shrink-0">
                      <UsersRound className="h-4 w-4 text-amber-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm">Moderator</span>
                        <span className="text-xs text-gray-500">(Chief of Medicine)</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">Synthesizes debate into consensus</p>
                    </div>
                  </div>
                  <div className="relative inline-block">
                    <select
                      value={grandRoundsConfig.moderatorModel}
                      onChange={(e) => setGrandRoundsConfig(prev => ({
                        ...prev,
                        moderatorModel: e.target.value as LLMProvider,
                      }))}
                      className="appearance-none rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2212%22%20height%3d%2212%22%20viewBox%3d%220%200%2012%2012%22%3e%3cpath%20fill%3d%22%236b7280%22%20d%3d%22M2%204l4%204%204-4%22%2f%3e%3c%2fsvg%3e')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
                    >
                      {GRAND_ROUNDS_MODELS.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Consensus Approach Selection */}
                <div className="border-t border-indigo-200 mt-4 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="h-4 w-4 text-indigo-600" />
                    <span className="font-medium text-gray-900 text-sm">Consensus Approach</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setGrandRoundsConfig(prev => ({ ...prev, promptVersion: 'v2' }))}
                      className={clsx(
                        'rounded-lg border-2 p-3 text-left transition-all',
                        grandRoundsConfig.promptVersion === 'v2'
                          ? 'border-indigo-500 bg-white ring-1 ring-indigo-500'
                          : 'border-gray-200 bg-white/60 hover:border-gray-300'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 text-sm">Joint Evidence</span>
                        {grandRoundsConfig.promptVersion === 'v2' && (
                          <Check className="h-4 w-4 text-indigo-600" />
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Best evidence wins. Experts cite sources and defer when presented with stronger evidence.
                      </p>
                      <span className="mt-2 inline-block rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700">
                        Recommended
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGrandRoundsConfig(prev => ({ ...prev, promptVersion: 'v1' }))}
                      className={clsx(
                        'rounded-lg border-2 p-3 text-left transition-all',
                        grandRoundsConfig.promptVersion === 'v1'
                          ? 'border-indigo-500 bg-white ring-1 ring-indigo-500'
                          : 'border-gray-200 bg-white/60 hover:border-gray-300'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 text-sm">Democracy</span>
                        {grandRoundsConfig.promptVersion === 'v1' && (
                          <Check className="h-4 w-4 text-indigo-600" />
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Majority opinion wins. Consensus based on agreement count across experts.
                      </p>
                      <span className="mt-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                        Legacy
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 3: Iterations/Summary */}
        {runType === 'MODEL_STABILITY' ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Repeat className="h-5 w-5 text-amber-600" />
              3. Number of Iterations
            </h2>
            <p className="mb-4 text-sm text-gray-500">
              Run the model multiple times to measure consistency and stability of responses.
            </p>

            <div className="flex items-center gap-4">
              <input
                type="range"
                min="2"
                max="10"
                value={iterations}
                onChange={(e) => setIterations(Number(e.target.value))}
                className="h-2 w-48 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-amber-600"
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={iterations}
                  onChange={(e) => {
                    const val = Math.max(2, Math.min(10, Number(e.target.value) || 2));
                    setIterations(val);
                  }}
                  className="w-16 rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
                <span className="text-sm text-gray-500">iterations</span>
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-400">
              Total executions: 1 model × {iterations} iterations = {iterations} executions
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Repeat className="h-5 w-5 text-amber-600" />
              3. Execution Summary
            </h2>
            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-sm text-gray-600">
                Each selected model will run <strong>once</strong> for comparison.
              </p>
              <p className="mt-2 text-xs text-gray-400">
                Total executions: {selectedModels.length} model{selectedModels.length !== 1 ? 's' : ''} × 1 iteration = {selectedModels.length} execution{selectedModels.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Run Details */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Play className="h-5 w-5 text-amber-600" />
            4. Run Details (Optional)
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Run Name</label>
              <input
                type="text"
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
                placeholder="Auto-generated if empty"
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any notes about this test run..."
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
            </div>
          </div>
        </div>

        {/* Summary & Submit */}
        <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-6">
          <div className="text-sm text-gray-600">
            {selectedTestCaseId && selectedModels.length > 0 ? (
              <>
                {runType === 'MODEL_COMPARISON' && selectedModels.length < 2 ? (
                  <span className="text-amber-600 font-medium">
                    Model Comparison requires at least 2 models. Please select another model or switch to Model Stability mode.
                  </span>
                ) : (
                  <>
                    This will create <strong>{selectedModels.length * iterations}</strong> execution
                    {selectedModels.length * iterations !== 1 ? 's' : ''} ({selectedModels.length} model{selectedModels.length !== 1 ? 's' : ''} × {iterations} iteration{iterations !== 1 ? 's' : ''}) for blind evaluation.
                    {selectedModels.includes('GRAND_ROUNDS') && (
                      <span className="ml-1 text-indigo-600">(includes Grand Rounds consensus)</span>
                    )}
                  </>
                )}
              </>
            ) : (
              'Select a test case and at least one model to continue.'
            )}
          </div>
          <button
            type="submit"
            disabled={
              !selectedTestCaseId ||
              selectedModels.length === 0 ||
              (runType === 'MODEL_COMPARISON' && selectedModels.length < 2) ||
              isPending ||
              !isSelectedTestCaseValid
            }
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isEditing ? (
              <Save className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {isEditing ? 'Save Changes' : 'Create Test Run'}
          </button>
        </div>

        {/* Error Display */}
        {(createMutation.isError || updateMutation.isError) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">
                Failed to {isEditing ? 'update' : 'create'} test run
              </span>
            </div>
            <p className="mt-1 text-sm text-red-600">
              {(createMutation.error || updateMutation.error) instanceof Error
                ? (createMutation.error || updateMutation.error)?.message
                : 'An unexpected error occurred'}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
